import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaWhatsapp } from 'react-icons/fa';
import BranchSelector from '../components/BranchSelector';
import { getSelectedBranch, generateWhatsAppLink, getUpdatedBranches } from '../utils/branchesData';
import { getProductsByCategory } from '../services/api';
import './CategoryPage.css';

function PrescriptionDrugs() {
  const navigate = useNavigate();
  const [selectedBranch, setSelectedBranch] = useState(getSelectedBranch());
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const handleBranchChange = (event) => {
      setSelectedBranch(event.detail);
    };
    
    const handleBranchUpdate = () => {
      // Refresh selected branch data when branches are updated
      const updatedBranch = getSelectedBranch();
      setSelectedBranch(updatedBranch);
    };
    
    window.addEventListener('branchChanged', handleBranchChange);
    window.addEventListener('branchesUpdated', handleBranchUpdate);
    
    return () => {
      window.removeEventListener('branchChanged', handleBranchChange);
      window.removeEventListener('branchesUpdated', handleBranchUpdate);
    };
  }, []);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        // Prescription Drugs category has ID 1
        const productsData = await getProductsByCategory(1);
        // Show all products; normalize display names (remove trailing numbers and standardize casing)
        const normalizeName = (name) => (name || '').replace(/\s*\d+$/,'').trim();
        const toTitleCase = (s) => s.replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase());
        const standardizeDisplayName = (name) => {
          const base = normalizeName(name).replace(/[-_]+/g, ' ').replace(/\s+/g, ' ').trim().toLowerCase();
          const map = {
            'antidiabetic': 'Anti Diabetics',
            'anti diabetic': 'Anti Diabetics',
            'antiepileptic': 'Anti epileptics',
            'anti epileptic': 'Anti epileptics',
            'cholesterol medicines': 'Cholesterol medicines',
            'corticosteroids creams': 'Corticosteroids creams',
            'antibiotics': 'Antibiotics',
            'painkillers': 'Pain Killers',
            'pain killers': 'Pain Killers',
            'anti hypertensive': 'Anti-Hypertensive',
            'anti-hypertensive': 'Anti-Hypertensive',
            'anti hypertensives': 'Anti hypertensives',
            'anti gout': 'Anti Gout'
          };
          return map[base] || toTitleCase(base);
        };
        const allProducts = productsData.map(p => ({ ...p, name: standardizeDisplayName(p.name) }));
        setProducts(allProducts);
      } catch (error) {
        console.error('Error fetching products:', error);
        setProducts([]);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, []);

  return (
    <div className="category-page">
      <div className="category-container">
        <div className="category-top-bar">
          <button className="back-button" onClick={() => navigate('/products')}>
            <FaArrowLeft /> Back to Products
          </button>
          <BranchSelector />
        </div>

        <div className="category-page-header">
          <h1>Prescription Drugs</h1>
          <p className="category-page-subtitle">
            Visit us when a doctor or medical expert gives you a prescription. We offer a comprehensive range of prescription medications.
          </p>
        </div>

        <div className="category-products-grid">
          {loading ? (
            <div className="loading-state">
              <div className="loading-spinner"></div>
              <p>Loading products...</p>
            </div>
          ) : products.length > 0 ? (
            products.map((product, index) => (
              <div key={index} className="category-product-card">
                <div className="category-product-image">
                  <img 
                    src={product.image_url} 
                    alt={product.name}
                    loading="lazy"
                    onError={(e) => {
                      const img = e.currentTarget;
                      const name = (product.name || '').toLowerCase();
                      // Specific fix: Anti epileptics last image fallback
                      if (img.dataset.fix !== '1' && name.includes('epileptic')) {
                        img.dataset.fix = '1';
                        img.src = 'https://chisankho2025.com/products/PRISCRIPTIONS/9.jpg';
                        return;
                      }
                      if (img.dataset.fallback !== '1') {
                        img.dataset.fallback = '1';
                        img.src = '/pharmacare-logo.png';
                      }
                    }}
                  />
                </div>
                <div className="category-product-info">
                  <h3>{product.name}</h3>
                         {selectedBranch.hasWhatsApp ? (
                           <a 
                             href={generateWhatsAppLink(product.name, selectedBranch.whatsapp)}
                             target="_blank"
                             rel="noopener noreferrer"
                             className="inquire-button"
                           >
                             <FaWhatsapp /> Inquire on WhatsApp
                           </a>
                         ) : (
                           <div className="inquire-button disabled">
                             <FaWhatsapp /> WhatsApp Inquiries Not Available
                           </div>
                         )}
                </div>
              </div>
            ))
          ) : (
            <div className="empty-state">
              <p>No products found in this category.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default PrescriptionDrugs;